import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PulpPaperRoute } from '../../types';

/** 化机浆（CTMP）P&ID 详细图：洗涤 → 汽蒸 → 浸渍 → 反应仓 → 两段磨浆 → 消潜 → 漂白 */
const media = {
  pulp: { label: '木片 / 纸浆', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  chem: { label: 'Na₂SO₃ / H₂O₂', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
};

const devices = [
  { id: 'dc101', kind: 'hDrum' as const, x: 170, y: 189, tag: 'DC-101', name: '木片仓' },
  { id: 'w101', kind: 'washer' as const, x: 330, y: 188, tag: 'W-101', name: '木片洗涤' },
  { id: 's101', kind: 'vDrum' as const, x: 490, y: 154, tag: 'S-101', name: '汽蒸仓', note: '~80℃' },
  { id: 'i101', kind: 'digester' as const, x: 650, y: 115, tag: 'I-101', name: '化学浸渍器', note: 'Na₂SO₃' },
  { id: 'r101', kind: 'tower' as const, x: 810, y: 125, tag: 'R-101', name: '反应仓', note: '80~90℃' },
  { id: 'm101', kind: 'refiner' as const, x: 970, y: 179, tag: 'M-101', name: '一段磨浆', note: '120~130℃' },
  { id: 'd101', kind: 'hDrum' as const, x: 1120, y: 189, tag: 'D-101', name: '消潜池' },
  { id: 'm102', kind: 'refiner' as const, x: 1300, y: 186, tag: 'M-102', name: '二段磨浆' },
  { id: 'w102', kind: 'washer' as const, x: 1450, y: 188, tag: 'W-102', name: '筛选除渣' },
  { id: 'b101', kind: 'tower' as const, x: 1600, y: 125, tag: 'B-101', name: '漂白塔', note: 'H₂O₂' },
];

const ports: PidPort[] = [
  { id: 'p-wood', label: '木片', medium: 'pulp', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-pulp', label: '化机浆', medium: 'pulp', x: 1770, y: 224, direction: 'out' as const },
  { id: 'p-chem', label: 'Na₂SO₃', medium: 'chem', x: 630, y: 40, direction: 'in' as const },
  { id: 'p-chem2', label: 'H₂O₂', medium: 'chem', x: 1560, y: 40, direction: 'in' as const },
  { id: 'p-stm', label: '蒸汽', medium: 'steam', x: 450, y: 40, direction: 'in' as const },
  { id: 'p-water', label: '洗涤水', medium: 'water', x: 290, y: 120, direction: 'in' as const },
];

const pipes: PidPipe[] = [
  { id: 'k01', from: 'p-wood', fromSide: 'right', to: 'dc101', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'dc101', fromSide: 'right', to: 'w101', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'w101', fromSide: 'right', to: 's101', toSide: 'left', medium: 'pulp' },
  { id: 'k04', from: 's101', fromSide: 'right', to: 'i101', toSide: 'left', medium: 'pulp' },
  { id: 'k05', from: 'i101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'pulp' },
  { id: 'k06', from: 'r101', fromSide: 'right', to: 'm101', toSide: 'left', medium: 'pulp' },
  { id: 'k07', from: 'm101', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'pulp' },
  { id: 'k08', from: 'd101', fromSide: 'right', to: 'm102', toSide: 'left', medium: 'pulp' },
  { id: 'k09', from: 'm102', fromSide: 'right', to: 'w102', toSide: 'left', medium: 'pulp' },
  { id: 'k10', from: 'w102', fromSide: 'right', to: 'b101', toSide: 'left', medium: 'pulp' },
  { id: 'k11', from: 'b101', fromSide: 'right', to: 'p-pulp', toSide: 'left', medium: 'pulp' },
  { id: 'k12', from: 'p-chem', fromSide: 'right', to: 'i101', toSide: 'top', medium: 'chem' },
  { id: 'k13', from: 'p-chem2', fromSide: 'right', to: 'b101', toSide: 'top', medium: 'chem' },
  { id: 'k14', from: 'p-stm', fromSide: 'right', to: 's101', toSide: 'top', medium: 'steam' },
  { id: 'k15', from: 'p-water', fromSide: 'right', to: 'w101', toSide: 'top', medium: 'water' },
];

const ChemimechanicalDiagram: React.FC<{ route: PulpPaperRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="化机浆（CTMP）装置详细流程"
    subtitle="木片洗涤 → 汽蒸 → 化学浸渍（Na₂SO₃）→ 反应仓 → 两段压力磨浆（120~130℃）→ 消潜 → 筛选 → H₂O₂ 漂白，得率 80~95%（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default ChemimechanicalDiagram;
